import { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Save } from "lucide-react";
import { PointerSensor, useSensor, useSensors } from "@dnd-kit/core";
import { arrayMove } from "@dnd-kit/sortable";
import {
  getProductBySlug, updateProduct, createProduct, updateProductGallery,
  uploadProductGalleryImage, uploadImage, formatApiErrorDetail, listShippingRates,
} from "@/admin/adminApi";
import { GalleryManager } from "@/admin/GalleryManager";
import { useImageCrop } from "@/admin/useImageCrop";
import { CATEGORY_CONFIGS } from "@/admin/categoryConfig";
import { BasicInfoSection } from "@/admin/product-edit-sections/BasicInfoSection";
import { PricingSection } from "@/admin/product-edit-sections/PricingSection";
import { ProductMetaSection } from "@/admin/product-edit-sections/ProductMetaSection";
import { SeoSection } from "@/admin/product-edit-sections/SeoSection";
import { OptionGroupsSection } from "@/admin/product-edit-sections/OptionGroupsSection";
import { FeaturesSection } from "@/admin/product-edit-sections/FeaturesSection";
import { SpecsSection } from "@/admin/product-edit-sections/SpecsSection";
import { SizesSection } from "@/admin/product-edit-sections/SizesSection";
import { ContentSectionsSection } from "@/admin/product-edit-sections/ContentSectionsSection";
import { HeroPickerSection } from "@/admin/product-edit-sections/HeroPickerSection";
import { LABEL_CLASS } from "@/admin/product-edit-sections/shared";

const slugify = (str) => str.toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "");

const EMPTY_FORM = {
  slug: "", name: "", type: "", price: "", priceWas: "", priceFrom: false, priceOnRequest: false,
  badge: "", availability: "", tagline: "", thumb: "", intro: "", features: [], specs: [],
  sizes: [], seasoningRequired: false, vehicle: "", variants: "", careNote: "",
  seoTitle: "", seoDescription: "", inStock: true, optionGroups: [], variantStock: [], contentSections: [],
  shippingRateId: "",
};

export default function AdminProductEditPage() {
  const { categorySlug, slug: slugParam } = useParams();
  const config = CATEGORY_CONFIGS[categorySlug];
  const isNew = slugParam === "new";
  const navigate = useNavigate();
  const [mongoId, setMongoId] = useState(null);
  const [slugTouched, setSlugTouched] = useState(false);
  const [form, setForm] = useState({ ...EMPTY_FORM, type: config?.typeOptions?.[0]?.value || "" });
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [thumbUploading, setThumbUploading] = useState(false);
  const [error, setError] = useState("");
  const [newFeature, setNewFeature] = useState("");
  const [newSpecKey, setNewSpecKey] = useState("");
  const [newSpecValue, setNewSpecValue] = useState("");
  const [shippingRates, setShippingRates] = useState([]);
  const optionGroupSensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  useEffect(() => { listShippingRates().then(setShippingRates); }, []);

  const optionalShippingRates = useMemo(() => shippingRates.filter((r) => !r.isDefault), [shippingRates]);
  const heroPickerGallery = useMemo(() => gallery.filter((g) => g.type !== "video"), [gallery]);

  const handleField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const handleThumbUpload = async (file) => {
    setThumbUploading(true);
    setError("");
    try {
      const { url } = await uploadImage(file);
      handleField("thumb", url);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setThumbUploading(false);
    }
  };
  const thumbCrop = useImageCrop(handleThumbUpload);

  useEffect(() => {
    if (!config || isNew) return;
    getProductBySlug(config.category, slugParam).then((data) => {
      setMongoId(data.id);
      setForm({
        slug: data.slug, name: data.name, type: data.type || "",
        price: data.price ?? "", priceWas: data.priceWas ?? "", priceFrom: !!data.priceFrom, priceOnRequest: !!data.priceOnRequest,
        badge: data.badge || "", availability: data.availability || "", tagline: data.tagline || "",
        thumb: data.thumb, intro: data.intro || "", features: data.features || [], specs: data.specs || [],
        sizes: data.sizes || [], seasoningRequired: data.seasoningRequired === true,
        vehicle: data.vehicle || "", variants: data.variants || "", careNote: data.careNote || "",
        seoTitle: data.seoTitle || "", seoDescription: data.seoDescription || "",
        inStock: data.inStock !== false, optionGroups: data.optionGroups || [], variantStock: data.variantStock || [],
        contentSections: data.contentSections || [],
        shippingRateId: data.shippingRateId || "",
        hero: data.hero || "",
        heroFocalMobile: data.heroFocalMobile || "",
        heroPosition: data.heroPosition || "",
      });
      setGallery(data.gallery || []);
      setLoading(false);
    });
  }, [config, slugParam, isNew]);

  if (!config) return <p className="text-red-400 font-mono text-sm">Unknown category "{categorySlug}".</p>;

  const handleNameChange = (value) => {
    setForm((f) => ({ ...f, name: value, slug: slugTouched ? f.slug : slugify(value) }));
  };

  const handleSlugChange = (value) => {
    setSlugTouched(true);
    setForm((f) => ({ ...f, slug: slugify(value) }));
  };

  const handleGalleryChange = async (newGallery) => {
    setGallery(newGallery);
    await updateProductGallery(config.category, mongoId, newGallery);
  };

  const handleUpload = async (file) => {
    setUploading(true);
    try {
      const { gallery: updated } = await uploadProductGalleryImage(config.category, mongoId, file);
      setGallery(updated);
      return updated;
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setUploading(false);
    }
  };

  const handleSetHero = async (src) => {
    setForm((f) => ({ ...f, hero: src }));
    await updateProduct(config.category, mongoId, { hero: src });
  };

  const handleSetHeroFocal = async (val) => {
    setForm((f) => ({ ...f, heroFocalMobile: val }));
    await updateProduct(config.category, mongoId, { heroFocalMobile: val });
  };

  const handleSetHeroPosition = async (val) => {
    setForm((f) => ({ ...f, heroPosition: val }));
    await updateProduct(config.category, mongoId, { heroPosition: val });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError("");
    if (isNew && !form.thumb) {
      setError("Please upload a main photo before creating the product.");
      return;
    }
    setSaving(true);
    try {
      if (isNew) {
        const created = await createProduct(config.category, {
          slug: form.slug, name: form.name, type: form.type || null,
          price: form.price === "" ? 0 : Number(form.price), priceFrom: form.priceFrom, priceOnRequest: form.priceOnRequest,
          badge: form.badge || null, availability: form.availability || null, tagline: form.tagline || null,
          thumb: form.thumb, intro: form.intro || null, features: form.features, inStock: form.inStock,
          shippingRateId: form.shippingRateId || null,
        });
        navigate(`/admin/${categorySlug}/${created.slug}`);
      } else {
        await updateProduct(config.category, mongoId, {
          name: form.name, type: form.type || null,
          price: form.price === "" ? null : Number(form.price), priceWas: form.priceWas === "" ? null : Number(form.priceWas),
          priceFrom: form.priceFrom, priceOnRequest: form.priceOnRequest, badge: form.badge || null,
          availability: form.availability || null, tagline: form.tagline || null, intro: form.intro || null,
          features: form.features, specs: form.specs,
          sizes: form.sizes, seasoningRequired: form.seasoningRequired,
          vehicle: form.vehicle || null, variants: form.variants || null, careNote: form.careNote || null,
          optionGroups: form.optionGroups, variantStock: form.variantStock,
          contentSections: form.contentSections,
          seoTitle: form.seoTitle || null, seoDescription: form.seoDescription || null, inStock: form.inStock,
          shippingRateId: form.shippingRateId || null,
        });
        navigate(`/admin/${categorySlug}`);
      }
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const addFeature = () => {
    if (!newFeature.trim()) return;
    setForm((f) => ({ ...f, features: [...f.features, newFeature.trim()] }));
    setNewFeature("");
  };
  const removeFeature = (i) => setForm((f) => ({ ...f, features: f.features.filter((_, idx) => idx !== i) }));
  const updateFeature = (i, value) => setForm((f) => ({ ...f, features: f.features.map((feat, idx) => (idx === i ? value : feat)) }));

  const addSpec = () => {
    if (!newSpecKey.trim() || !newSpecValue.trim()) return;
    setForm((f) => ({ ...f, specs: [...f.specs, [newSpecKey.trim(), newSpecValue.trim()]] }));
    setNewSpecKey("");
    setNewSpecValue("");
  };
  const removeSpec = (i) => setForm((f) => ({ ...f, specs: f.specs.filter((_, idx) => idx !== i) }));
  const updateSpec = (i, col, value) => setForm((f) => ({
    ...f,
    specs: f.specs.map((row, idx) => (idx === i ? (col === 0 ? [value, row[1]] : [row[0], value]) : row)),
  }));

  const addSize = () => setForm((f) => ({ ...f, sizes: [...f.sizes, { label: "", priceDelta: 0, inStock: true, specs: [] }] }));
  const removeSize = (si) => setForm((f) => ({ ...f, sizes: f.sizes.filter((_, i) => i !== si) }));
  const updateSize = (si, key, value) => setForm((f) => ({ ...f, sizes: f.sizes.map((s, i) => (i === si ? { ...s, [key]: value } : s)) }));
  const addSizeSpec = (si) => setForm((f) => ({ ...f, sizes: f.sizes.map((s, i) => (i === si ? { ...s, specs: [...s.specs, ["", ""]] } : s)) }));
  const removeSizeSpec = (si, pi) => setForm((f) => ({ ...f, sizes: f.sizes.map((s, i) => (i === si ? { ...s, specs: s.specs.filter((_, j) => j !== pi) } : s)) }));
  const updateSizeSpec = (si, pi, col, value) => setForm((f) => ({
    ...f,
    sizes: f.sizes.map((s, i) => (i === si ? { ...s, specs: s.specs.map((row, j) => (j === pi ? (col === 0 ? [value, row[1]] : [row[0], value]) : row)) } : s)),
  }));

  const addOptionGroup = () => {
    setForm((f) => ({
      ...f,
      optionGroups: [...f.optionGroups, { key: `group-${f.optionGroups.length + 1}`, label: "", required: true, choices: [] }],
    }));
  };
  const removeOptionGroup = (gi) => setForm((f) => ({ ...f, optionGroups: f.optionGroups.filter((_, i) => i !== gi) }));
  const updateOptionGroup = (gi, key, value) => setForm((f) => ({
    ...f,
    optionGroups: f.optionGroups.map((g, i) => (i === gi ? { ...g, [key]: value, ...(key === "label" ? { key: slugify(value) || g.key } : {}) } : g)),
  }));
  const addChoice = (gi) => setForm((f) => ({
    ...f,
    optionGroups: f.optionGroups.map((g, i) => (i === gi ? { ...g, choices: [...g.choices, { label: "", priceDelta: 0, inStock: true }] } : g)),
  }));
  const removeChoice = (gi, ci) => setForm((f) => ({
    ...f,
    optionGroups: f.optionGroups.map((g, i) => (i === gi ? { ...g, choices: g.choices.filter((_, j) => j !== ci) } : g)),
  }));
  const updateChoice = (gi, ci, key, value) => setForm((f) => ({
    ...f,
    optionGroups: f.optionGroups.map((g, i) => (i === gi ? { ...g, choices: g.choices.map((c, j) => (j === ci ? { ...c, [key]: value } : c)) } : g)),
  }));
  const handleOptionGroupDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = Number(String(active.id).split("-").pop());
    const newIndex = Number(String(over.id).split("-").pop());
    setForm((f) => ({ ...f, optionGroups: arrayMove(f.optionGroups, oldIndex, newIndex) }));
  };
  const handleChoiceDragEnd = (gi) => (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = Number(String(active.id).split("-").pop());
    const newIndex = Number(String(over.id).split("-").pop());
    setForm((f) => ({
      ...f,
      optionGroups: f.optionGroups.map((g, i) => (i === gi ? { ...g, choices: arrayMove(g.choices, oldIndex, newIndex) } : g)),
    }));
  };

  // Full cartesian product of every group's choices — e.g. Fitment x Tent
  // Access Hatch — used to let the admin toggle stock per *combination*,
  // since a per-choice inStock flag can't express cross-group dependencies.
  const combinations = form.optionGroups
    .filter((g) => g.choices.length > 0)
    .reduce((acc, group) => {
      const rows = group.choices.map((c) => ({ [group.key]: c.label }));
      return acc.length === 0 ? rows : acc.flatMap((combo) => rows.map((row) => ({ ...combo, ...row })));
    }, []);
  const findComboEntry = (combo) => form.variantStock.find((v) => {
    const keys = Object.keys(v.combination);
    return keys.length === Object.keys(combo).length && keys.every((k) => v.combination[k] === combo[k]);
  });
  const toggleComboStock = (combo, checked) => setForm((f) => {
    const existing = findComboEntry(combo);
    if (!existing) return { ...f, variantStock: [...f.variantStock, { combination: combo, inStock: checked }] };
    return { ...f, variantStock: f.variantStock.map((v) => (v === existing ? { ...v, inStock: checked } : v)) };
  });

  const addContentSection = () => setForm((f) => ({ ...f, contentSections: [...f.contentSections, { heading: "", body: "", bullets: [], highlight: false, linkText: "", linkHref: "" }] }));
  const removeContentSection = (si) => setForm((f) => ({ ...f, contentSections: f.contentSections.filter((_, i) => i !== si) }));
  const updateContentSection = (si, key, value) => setForm((f) => ({
    ...f,
    contentSections: f.contentSections.map((s, i) => (i === si ? { ...s, [key]: value } : s)),
  }));
  const moveContentSection = (si, dir) => setForm((f) => {
    const newIndex = si + dir;
    if (newIndex < 0 || newIndex >= f.contentSections.length) return f;
    const sections = [...f.contentSections];
    [sections[si], sections[newIndex]] = [sections[newIndex], sections[si]];
    return { ...f, contentSections: sections };
  });
  const addSectionBullet = (si) => setForm((f) => ({
    ...f,
    contentSections: f.contentSections.map((s, i) => (i === si ? { ...s, bullets: [...s.bullets, ""] } : s)),
  }));
  const removeSectionBullet = (si, bi) => setForm((f) => ({
    ...f,
    contentSections: f.contentSections.map((s, i) => (i === si ? { ...s, bullets: s.bullets.filter((_, j) => j !== bi) } : s)),
  }));
  const updateSectionBullet = (si, bi, value) => setForm((f) => ({
    ...f,
    contentSections: f.contentSections.map((s, i) => (i === si ? { ...s, bullets: s.bullets.map((b, j) => (j === bi ? value : b)) } : s)),
  }));

  if (loading) return <p className="text-white/60 font-mono text-sm">Loading…</p>;

  return (
    <div data-testid="admin-product-edit-page" className="max-w-3xl">
      <Link to={`/admin/${categorySlug}`} data-testid="admin-edit-back-link" className="inline-flex items-center gap-1.5 text-white/60 hover:text-white text-sm mb-6">
        <ArrowLeft size={14} /> Back to list
      </Link>
      <h1 className="font-display text-3xl text-white mb-6">{isNew ? `Add ${config.label} Product` : `Edit — ${form.name}`}</h1>

      {error && <p data-testid="admin-edit-error" className="text-red-400 text-sm mb-4 font-mono">{error}</p>}

      <form onSubmit={handleSave} className="space-y-6">
        <BasicInfoSection
          isNew={isNew} config={config} form={form} thumbUploading={thumbUploading} thumbCrop={thumbCrop}
          handleNameChange={handleNameChange} handleSlugChange={handleSlugChange} handleField={handleField}
        />

        <PricingSection
          isNew={isNew} config={config} form={form} optionalShippingRates={optionalShippingRates}
          shippingRates={shippingRates} handleField={handleField} updateSize={updateSize}
        />

        <ProductMetaSection config={config} form={form} handleField={handleField} />

        <SeoSection isNew={isNew} form={form} handleField={handleField} />

        <OptionGroupsSection
          isNew={isNew} form={form} optionGroupSensors={optionGroupSensors} combinations={combinations}
          findComboEntry={findComboEntry} toggleComboStock={toggleComboStock} addOptionGroup={addOptionGroup}
          removeOptionGroup={removeOptionGroup} updateOptionGroup={updateOptionGroup} addChoice={addChoice}
          removeChoice={removeChoice} updateChoice={updateChoice} handleOptionGroupDragEnd={handleOptionGroupDragEnd}
          handleChoiceDragEnd={handleChoiceDragEnd}
        />

        <FeaturesSection
          form={form} newFeature={newFeature} setNewFeature={setNewFeature} addFeature={addFeature}
          removeFeature={removeFeature} updateFeature={updateFeature} showNumbers={config.category === "bushcabin"}
        />

        <SpecsSection
          form={form} newSpecKey={newSpecKey} setNewSpecKey={setNewSpecKey} newSpecValue={newSpecValue}
          setNewSpecValue={setNewSpecValue} addSpec={addSpec} removeSpec={removeSpec} updateSpec={updateSpec}
        />

        <SizesSection
          isNew={isNew} config={config} form={form} addSize={addSize} removeSize={removeSize} updateSize={updateSize}
          addSizeSpec={addSizeSpec} removeSizeSpec={removeSizeSpec} updateSizeSpec={updateSizeSpec}
        />

        <ContentSectionsSection
          isNew={isNew} form={form} addContentSection={addContentSection} removeContentSection={removeContentSection}
          updateContentSection={updateContentSection} moveContentSection={moveContentSection}
          addSectionBullet={addSectionBullet} removeSectionBullet={removeSectionBullet}
          updateSectionBullet={updateSectionBullet}
        />

        {!isNew && (
          <div>
            <span className={LABEL_CLASS}>Photo & Video Gallery — drag to reorder (videos can go anywhere), tag icon to edit alt text</span>
            <div className="mt-2">
              <GalleryManager gallery={gallery} onChange={handleGalleryChange} onUpload={handleUpload} uploading={uploading} allowVideo={config.hasVideo} />
            </div>
          </div>
        )}

        <HeroPickerSection
          isNew={isNew} config={config} form={form} heroPickerGallery={heroPickerGallery}
          handleSetHero={handleSetHero} handleSetHeroFocal={handleSetHeroFocal} handleSetHeroPosition={handleSetHeroPosition}
        />

        <button data-testid="admin-save-button" type="submit" disabled={saving} className="btn-primary disabled:opacity-50">
          <Save size={14} /> {saving ? "Saving…" : isNew ? "Create Product" : "Save Changes"}
        </button>
      </form>
    </div>
  );
}
